import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { useContent } from "@/lib/content-context";
import { ArrowRight, Coffee, Heart, Users, Sparkles, BookOpen, Home, Mail } from "lucide-react";

const ICON_MAP = { Coffee, Heart, Users, Sparkles, BookOpen, Home };

const FALLBACK = {
    eyebrow: "Careers",
    title: "Brew with us.",
    intro: "Hideout is built by a small, opinionated team that loves coffee, food, and hospitality. If those three words light you up, we'd love to hear from you.",
    contact_email: "careers@cafehideout.in",
    values: [],
    roles_heading: "Who we're looking for.",
    roles: [],
    form_heading: "Tell us about you.",
};

export default function Career() {
    const { content } = useContent();
    const c = { ...FALLBACK, ...(content?.careers || {}) };
    const roles = c.roles || [];
    const values = c.values || [];

    const [form, setForm] = useState({
        name: "", email: "", phone: "", city: "",
        role: roles[0]?.title || "", message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!form.role && roles[0]?.title) {
            setForm((f) => ({ ...f, role: roles[0].title }));
        }
    }, [roles, form.role]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Name and email are required");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/careers", form);
            toast.success("Thanks. We'll get back over coffee.");
            setForm({ name: "", email: "", phone: "", city: "", role: roles[0]?.title || "", message: "" });
        } catch {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-cream-light pt-28" data-testid="career-page">
            <SEO title="Careers" description="Work at Cafe Hideout — open roles across Bengaluru, Mumbai, and Pune." path="/careers" />

            {/* Hero */}
            <section className="px-6 md:px-12 py-16 md:py-24">
                <div className="max-w-[1200px] mx-auto">
                    <Reveal>
                        <div className="tracked text-coffee/50 text-[10px] mb-6">{c.eyebrow}</div>
                        <h1 className="font-editorial font-bold text-coffee leading-[0.95]" style={{ fontSize: "clamp(2.6rem, 7vw, 6rem)" }}>
                            {c.title}
                        </h1>
                        <p className="font-body text-coffee/80 mt-8 max-w-2xl text-lg leading-relaxed whitespace-pre-wrap">
                            {c.intro}
                        </p>
                        {c.contact_email && (
                            <a
                                href={`mailto:${c.contact_email}`}
                                className="inline-flex items-center gap-2 mt-6 tracked text-[11px] text-gold gold-underline"
                                data-testid="career-contact-email"
                            >
                                <Mail size={14} /> {c.contact_email}
                            </a>
                        )}
                    </Reveal>
                </div>
            </section>

            {/* Values */}
            {values.length > 0 && (
                <section className="px-6 md:px-12 pb-16 md:pb-24">
                    <div className="max-w-[1200px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 border-y border-coffee/15 py-10">
                        {values.map((v, i) => {
                            const Icon = ICON_MAP[v.icon] || Sparkles;
                            return (
                                <Reveal key={i} delay={i * 0.07}>
                                    <Icon size={24} strokeWidth={1.2} className="text-gold mb-3" />
                                    <div className="font-editorial font-medium text-coffee text-lg md:text-xl">{v.label}</div>
                                </Reveal>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Open roles */}
            {roles.length > 0 && (
                <section className="px-6 md:px-12 pb-16 md:pb-24">
                    <div className="max-w-[1200px] mx-auto">
                        <Reveal>
                            <div className="tracked text-coffee/50 text-[10px] mb-5">Open Roles</div>
                            <h2 className="font-editorial font-medium text-coffee text-3xl md:text-5xl mb-10" style={{ letterSpacing: "-0.005em" }}>
                                {c.roles_heading}
                            </h2>
                        </Reveal>
                        <div className="space-y-2">
                            {roles.map((r, i) => (
                                <Reveal key={i} delay={i * 0.04}>
                                    <div className="group bg-white border border-coffee/10 hover:border-coffee transition-colors px-6 py-6 flex items-center justify-between gap-6 flex-wrap">
                                        <div>
                                            <div className="font-mono tracked text-gold text-[10px] mb-1">{r.type}</div>
                                            <h3 className="font-editorial font-medium text-coffee text-2xl md:text-3xl">{r.title}</h3>
                                            <div className="font-body text-coffee/60 text-sm mt-1">{r.city}</div>
                                        </div>
                                        <button
                                            onClick={() => { setForm((f) => ({ ...f, role: r.title })); document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" }); }}
                                            className="btn-pill btn-pill-coffee text-[10px]"
                                            data-testid={`apply-${r.title.toLowerCase().replace(/\W+/g, "-")}`}
                                        >
                                            Apply <ArrowRight size={14} />
                                        </button>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Form */}
            <section id="apply-form" className="px-6 md:px-12 py-20 md:py-28 bg-cream">
                <div className="max-w-[800px] mx-auto">
                    <Reveal>
                        <div className="tracked text-coffee/50 text-[10px] mb-5">Send Your Story</div>
                        <h2 className="font-editorial font-bold text-coffee text-4xl md:text-5xl mb-12">{c.form_heading}</h2>
                    </Reveal>
                    <form onSubmit={submit} className="space-y-8" data-testid="career-form">
                        <FormField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="career-name" />
                        <FormField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="career-email" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <FormField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="career-phone" />
                            <FormField label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="career-city" />
                        </div>
                        <div>
                            <label className="tracked text-coffee/60 text-[10px] block mb-3">Role</label>
                            <select
                                value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                                data-testid="career-role"
                                className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none"
                            >
                                {roles.map((r) => <option key={r.title}>{r.title}</option>)}
                                <option>Other</option>
                            </select>
                        </div>
                        <FormField label="A few lines about you" textarea value={form.message} onChange={(v) => setForm({ ...form, message: v })} testid="career-message" />
                        <button type="submit" disabled={submitting} className="btn-pill btn-pill-coffee disabled:opacity-50" data-testid="career-submit">
                            {submitting ? "Sending…" : "Send Application"} <ArrowRight size={14} />
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
}

function FormField({ label, value, onChange, type = "text", textarea = false, testid }) {
    return (
        <div>
            <label className="tracked text-coffee/60 text-[10px] block mb-3">{label}</label>
            {textarea ? (
                <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4} data-testid={testid}
                    className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none resize-none" />
            ) : (
                <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                    className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none" />
            )}
        </div>
    );
}
